/**
 * The filter row and the list/card toggle, shared by My raves and All raves.
 *
 * Both screens filter the same object on the same axes, so they use the same
 * control — a second copy is how two screens end up disagreeing about what
 * "upcoming" means.
 *
 * **The view choice is remembered**, platform-scoped (`myrave:platform:raveView`), not
 * per edition: it is a preference about how somebody reads a list, not
 * anything to do with one rave. It goes through `lib/storage`, never
 * `localStorage` directly — hard rule 6, and that helper is also what survives
 * a browser with site data blocked.
 *
 * **Past raves are hidden until asked for** (the user's decision,
 * 2026-09-28): an edition that has ended is a PAST rave, and a person sees
 * one only when they chose to. So the default filter is `upcoming`, which
 * means "not over yet" — running now, to come, or dates still to be
 * announced — and `past` and `all` are a tap away.
 *
 * **A visitor's list always OPENS on Upcoming** and is always in date order,
 * the closest rave first (the user, 2026-10-21: "find a rave default should
 * open on upcoming, always order by dates, closest ones first"). The filter
 * a visitor picks lasts for the visit, not across them. An ADMIN list still
 * remembers its own (`myrave:platform:raveFilterAdmin`).
 */
import { useState } from 'react';
import { Row, Txt } from '@/ui';
import { useT } from '@/i18n';
import { readJson, writeJson } from '@/lib/storage';
import type { Rave, Timing } from './RaveCard';
import { timingOf } from './RaveCard';
import { sortRaves } from './RaveTable';
import './RaveFilters.css';

export type RaveView = 'card' | 'list';
export type RaveFilter = 'all' | 'upcoming' | 'live' | 'past' | 'clashes';

const VIEW_KEY = 'raveView';
const ADMIN_FILTER_KEY = 'raveFilterAdmin';
const FILTERS: readonly RaveFilter[] = ['all', 'upcoming', 'live', 'past', 'clashes'];

/** What a list shows before anybody has chosen: the raves that are not over. */
export const DEFAULT_FILTER: RaveFilter = 'upcoming';

/**
 * The filter, remembered platform-wide. `clashes` is only offered where the
 * raves carry counters, so a remembered `clashes` on a list without them
 * falls back to the default rather than filtering everything away.
 */
export function useRaveFilter(withClashes?: boolean, admin?: boolean): [RaveFilter, (f: RaveFilter) => void] {
  /*
   * An ADMIN list (rave admin, console) opens on "All" and remembers its own
   * choice: an organiser manages last year's edition too, and hiding it behind
   * "Past" is a visitor's default, not theirs (2026-09-28). Separate keys, so
   * a visitor's choice never leaks into the admin's view or back.
   */
  const fallback: RaveFilter = admin ? 'all' : DEFAULT_FILTER;
  const [filter, set] = useState<RaveFilter>(() => {
    if (!admin) return fallback;
    const stored = readJson<RaveFilter>(null, ADMIN_FILTER_KEY, fallback);
    if (!FILTERS.includes(stored)) return fallback;
    return stored === 'clashes' && !withClashes ? fallback : stored;
  });
  return [filter, (f) => { set(f); if (admin) writeJson(null, ADMIN_FILTER_KEY, f); }];
}

export function useRaveView(): [RaveView, (v: RaveView) => void] {
  const [view, set] = useState<RaveView>(() => (readJson<RaveView>(null, VIEW_KEY, 'card')));
  return [view, (v) => { set(v); writeJson(null, VIEW_KEY, v); }];
}

/** Does this rave pass the filter, given when it is? */
export function matchesFilter(r: Rave, t: Timing, f: RaveFilter): boolean {
  switch (f) {
    // Not over yet — INCLUDING one running now. This is the default list,
    // and a rave on right now is the last one to hide from it.
    case 'upcoming': return t.tone === 'soon' || t.tone === 'tbc' || t.tone === 'live';
    case 'live': return t.tone === 'live';
    case 'past': return t.tone === 'past';
    // Only meaningful where counters exist; on All raves it is not offered.
    case 'clashes': return (r.clashes ?? 0) > 0;
    default: return true;
  }
}

/**
 * The raves the filter passes, closest to now first: soonest first, and on
 * "Past" the most recent first. A rave with no date yet goes last.
 */
export function applyFilter(raves: Rave[], f: RaveFilter, now: number): Rave[] {
  const passed = raves.filter((r) => matchesFilter(r, timingOf(r, now), f));
  return sortRaves(passed, 'date', f === 'past' ? 'desc' : 'asc');
}

function Chip({ label, active, count, onClick }: {
  label: string; active: boolean; count?: number; onClick: () => void;
}) {
  return (
    <button onClick={onClick} aria-pressed={active} className="rave-filters-chip">
      {label}
      {count !== undefined && (
        <Txt size={10.5} weight={700} tabular tone={active ? 'inherit' : 'faint'} className={active ? 'rave-filters-count-on' : undefined}>
          {count}
        </Txt>
      )}
    </button>
  );
}

export function ViewToggle({ view, onChange }: { view: RaveView; onChange: (v: RaveView) => void }) {
  const tr = useT();
  const opts: { id: RaveView; label: string; d: string }[] = [
    { id: 'card', label: tr('Card view'), d: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z' },
    { id: 'list', label: tr('List view'), d: 'M4 6h16M4 12h16M4 18h16' },
  ];
  return (
    <Row gap={2} className="rave-filters-toggle">
      {opts.map((o) => (
        <button
          key={o.id}
          onClick={() => onChange(o.id)}
          aria-label={o.label}
          aria-pressed={view === o.id}
          className="rave-filters-toggle-btn"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={o.d} /></svg>
        </button>
      ))}
    </Row>
  );
}

export function RaveFilters({
  raves, now, filter, onFilter, view, onView, withClashes,
}: {
  raves: Rave[]; now: number;
  filter: RaveFilter; onFilter: (f: RaveFilter) => void;
  view?: RaveView; onView?: (v: RaveView) => void;
  /** Offer the clashes filter. Only where the raves carry counters. */
  withClashes?: boolean;
}) {
  const tr = useT();
  const count = (f: RaveFilter) => applyFilter(raves, f, now).length;

  // The default first; "All" last, since it is the one that brings past
  // raves back in with everything else.
  const chips: { id: RaveFilter; label: string }[] = [
    { id: 'upcoming', label: tr('Upcoming') },
    { id: 'live', label: tr('On now') },
    { id: 'past', label: tr('Past') },
    ...(withClashes ? [{ id: 'clashes' as const, label: tr('With clashes') }] : []),
    { id: 'all', label: tr('All') },
  ];

  return (
    <div className="ui-row rave-filters-bar">
      {/*
        * Scrolls rather than wraps: five chips plus a toggle wrap to two rows
        * at 390px, and a filter row that changes height as you tap through it
        * pushes the list under your thumb.
        */}
      {/*
        * `minWidth: 0` is what lets this actually scroll. A flex item's
        * default `min-width: auto` is its CONTENT width, so `overflow-x:auto`
        * never engages — the row just grows and pushes the page sideways. At
        * 320px that was 17px of horizontal document scroll under the thumb.
        */}
      <Row gap={7} className="rave-filters-chips">
        {chips.map((c) => {
          const n = count(c.id);
          return (
            <Chip
              key={c.id}
              label={c.label}
              count={n}
              active={filter === c.id}
              onClick={() => onFilter(c.id)}
            />
          );
        })}
      </Row>
      {view && onView && <ViewToggle view={view} onChange={onView} />}
    </div>
  );
}
